/**
 * The rules a view can declare beyond its columns: a default sort, fixed filters that every reader gets and cannot
 * remove, and, on a board, the fields a reader may filter by. The shapes live here beside the helpers the validator,
 * the API and the CRM share: which field types each rule accepts, how a relative date resolves against a local day,
 * and how a rule reads in words on a chip or an approval card.
 */
import { z } from 'zod';

const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

/** Columns of the nodes row itself that a view may sort or date-filter by beside the type's own fields. */
export const VIEW_SYSTEM_FIELDS = ['created_at', 'updated_at'] as const;
export type ViewSystemField = (typeof VIEW_SYSTEM_FIELDS)[number];

export const VIEW_FILTER_OPS = ['eq', 'neq', 'in', 'isEmpty', 'isNotEmpty', 'before', 'after'] as const;
export type ViewFilterOp = (typeof VIEW_FILTER_OPS)[number];

/**
 * The value of a `before` or `after` filter: a calendar day (`2026-12-31`), `today`, or a day counted from today
 * (`+7d`, `-30d`; whole days only, at most 999). Relative values resolve when the view is read, in the reader's time
 * zone, so "before today" is overdue for whoever is looking.
 */
export const VIEW_DATE_VALUE_PATTERN = /^(\d{4}-\d{2}-\d{2}|today|[+-]\d{1,3}d)$/;
const RELATIVE_DAYS_PATTERN = /^([+-])(\d{1,3})d$/;
const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Field types a `before`/`after` filter and a date board filter accept. */
export const DATE_FIELD_TYPES: ReadonlySet<string> = new Set(['date', 'datetime']);
/** Field types a board may offer as a filter control: picklist and option selects, workspace users and dates. */
export const BOARD_FILTER_FIELD_TYPES: ReadonlySet<string> = new Set(['select', 'user', 'date', 'datetime']);

const ScalarValueSchema = z.union([z.string().min(1).max(200), z.number().finite(), z.boolean()]);

export const AppViewFilterSchema = z
  .object({
    field: z.string().regex(FIELD_KEY_PATTERN, 'filter fields are snake_case field keys'),
    op: z.enum(VIEW_FILTER_OPS),
    value: z.union([ScalarValueSchema, z.array(z.string().min(1).max(200)).min(1).max(50)]).optional(),
  })
  .strict()
  .superRefine((filter, ctx) => {
    const issue = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message: `filter on "${filter.field}": ${message}` });
    switch (filter.op) {
      case 'eq':
      case 'neq':
        if (filter.value === undefined || Array.isArray(filter.value)) issue(`${filter.op} takes one value`);
        break;
      case 'in':
        if (!Array.isArray(filter.value)) issue('in takes a list of values');
        break;
      case 'isEmpty':
      case 'isNotEmpty':
        if (filter.value !== undefined) issue(`${filter.op} takes no value`);
        break;
      case 'before':
      case 'after':
        if (typeof filter.value !== 'string' || !VIEW_DATE_VALUE_PATTERN.test(filter.value)) issue(`${filter.op} takes a day (2026-12-31), "today", or days from today such as "+7d" or "-7d"`);
        break;
    }
  });

export const AppViewSortSchema = z
  .object({
    field: z.string().regex(FIELD_KEY_PATTERN, 'sort fields are snake_case field keys'),
    direction: z.enum(['asc', 'desc']),
  })
  .strict();

export type AppViewFilter = z.infer<typeof AppViewFilterSchema>;
export type AppViewSort = z.infer<typeof AppViewSortSchema>;

/** What a rule check needs to know about a field: its key and type. Lookup fields are edges, so no rule may use them. */
export interface RuleField {
  key: string;
  type: string;
}

export interface ViewRules {
  defaultSort?: AppViewSort;
  filters?: readonly AppViewFilter[];
  boardFilters?: readonly string[];
}

export function isViewSystemField(field: string): field is ViewSystemField {
  return (VIEW_SYSTEM_FIELDS as readonly string[]).includes(field);
}

/**
 * Every reason a view's rules do not fit its object type, as messages opening with `subject` (`view "overdue"` in a
 * manifest, `viewFilters` in a list request). A field must be one
 * of the type's own top-level fields (never a lookup, never a dotted include column) or, for sorting and date
 * comparisons, created_at or updated_at; before/after need a date field; a board filter needs a select, user or date
 * field. Used by the manifest validator and again by the API before it filters, so a request can never reach a prop the
 * type does not declare.
 */
export function viewRuleErrors(subject: string, objectType: string, fields: ReadonlyArray<RuleField>, rules: ViewRules): string[] {
  const errors: string[] = [];
  const byKey = new Map(fields.filter((field) => field.type !== 'lookup' && FIELD_KEY_PATTERN.test(field.key)).map((field) => [field.key, field]));
  const prefix = subject;

  if (rules.defaultSort && !byKey.has(rules.defaultSort.field) && !isViewSystemField(rules.defaultSort.field)) {
    errors.push(`${prefix}: defaultSort field "${rules.defaultSort.field}" is not a field of ${objectType}`);
  }

  for (const filter of rules.filters ?? []) {
    const field = byKey.get(filter.field);
    const isDateOp = filter.op === 'before' || filter.op === 'after';
    if (!field) {
      if (isDateOp && isViewSystemField(filter.field)) continue;
      errors.push(isViewSystemField(filter.field) ? `${prefix}: filter field "${filter.field}" only takes before or after` : `${prefix}: filter field "${filter.field}" is not a field of ${objectType}`);
      continue;
    }
    if (isDateOp && !DATE_FIELD_TYPES.has(field.type)) errors.push(`${prefix}: filter "${filter.field} ${filter.op}" needs a date or datetime field, and ${filter.field} is ${field.type}`);
  }

  const seen = new Set<string>();
  for (const key of rules.boardFilters ?? []) {
    if (seen.has(key)) errors.push(`${prefix}: boardFilters lists "${key}" twice`);
    seen.add(key);
    const field = byKey.get(key);
    if (!field) errors.push(`${prefix}: boardFilters field "${key}" is not a field of ${objectType}`);
    else if (!BOARD_FILTER_FIELD_TYPES.has(field.type)) errors.push(`${prefix}: boardFilters field "${key}" is ${field.type}; a board filters by select, user and date fields`);
  }
  return errors;
}

function addDays(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, date + days));
  return shifted.toISOString().slice(0, 10);
}

/**
 * A `before`/`after` value as the calendar day it means when `today` (YYYY-MM-DD, the reader's local day) is today.
 * Throws for a value outside the grammar, which the schema has already refused.
 */
export function resolveViewDate(value: string, today: string): string {
  if (!ISO_DAY_PATTERN.test(today)) throw new Error(`today must be a YYYY-MM-DD day, got "${today}"`);
  if (ISO_DAY_PATTERN.test(value)) return value;
  if (value === 'today') return today;
  const relative = RELATIVE_DAYS_PATTERN.exec(value);
  if (!relative) throw new Error(`"${value}" is not a view date: use YYYY-MM-DD, today, +Nd or -Nd`);
  const days = Number(relative[2]) * (relative[1] === '-' ? -1 : 1);
  return addDays(today, days);
}

/** The calendar day (YYYY-MM-DD) it is at `at` in `timeZone`, an IANA name; throws a RangeError for a name that is not one. */
export function localDay(at: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes): string => {
    const found = parts.find((candidate) => candidate.type === type);
    if (!found) throw new Error(`No ${type} formatting ${at.toISOString()} in ${timeZone}`);
    return found.value;
  };
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** A date value in words: "today", "today + 7 days", "today - 1 day", or the day itself. */
export function describeViewDate(value: string): string {
  if (value === 'today') return 'today';
  const relative = RELATIVE_DAYS_PATTERN.exec(value);
  if (!relative) return value;
  const days = Number(relative[2]);
  return `today ${relative[1] === '-' ? '-' : '+'} ${days} ${days === 1 ? 'day' : 'days'}`;
}

export interface DescribeFilterOptions {
  /** The field's label; its key when not given. */
  fieldLabel?: string;
  /** Turns a stored value into what the reader sees, such as a picklist item's label or a user's name. */
  valueLabel?: (value: string) => string;
}

/** One fixed filter in words, for a chip above a view or a line on an approval card: "Status is not Completed". */
export function describeViewFilter(filter: AppViewFilter, options: DescribeFilterOptions = {}): string {
  const label = options.fieldLabel ?? filter.field;
  const show = (value: unknown): string => (options.valueLabel ? options.valueLabel(String(value)) : String(value));
  switch (filter.op) {
    case 'eq':
      return `${label} is ${show(filter.value)}`;
    case 'neq':
      return `${label} is not ${show(filter.value)}`;
    case 'in':
      return `${label} is one of ${(Array.isArray(filter.value) ? filter.value : [filter.value]).map(show).join(', ')}`;
    case 'isEmpty':
      return `${label} is empty`;
    case 'isNotEmpty':
      return `${label} is set`;
    case 'before':
      return `${label} before ${describeViewDate(String(filter.value))}`;
    case 'after':
      return `${label} after ${describeViewDate(String(filter.value))}`;
  }
}

/** The rules of a view as short clauses for a summary line: "sorted by due_date asc", "only where ...", "board filters: ...". */
export function describeViewRules(rules: ViewRules): string[] {
  const clauses: string[] = [];
  if (rules.filters && rules.filters.length > 0) clauses.push(`only where ${rules.filters.map((filter) => describeViewFilter(filter)).join(' and ')}`);
  if (rules.defaultSort) clauses.push(`sorted by ${rules.defaultSort.field} ${rules.defaultSort.direction}`);
  if (rules.boardFilters && rules.boardFilters.length > 0) clauses.push(`board filters: ${rules.boardFilters.join(', ')}`);
  return clauses;
}
